import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import { CloudUpload, InsertDriveFile, Delete, Edit as EditIcon } from '@mui/icons-material';

import { IINSTRUCTIONS, AnnouncementAttachment } from 'types/auth';
import StepModify from './otherComponent/StepModify';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';
import FieldListBuilder, { InputComponents } from './FormComponents/FieldListBuilder';
import AttachmentUploader from 'pages/announcements/components/AttachmentUploader';
import useApi from 'hooks/useApi';
import { toActorUids } from '../../utils/actor';

// Types offered on an INSTRUCTION step. The signature is an ESIGN field like
// any other, so it is configured, ordered and positioned the same way — it
// replaces the old settings.isEsignRequired flag.
const INSTRUCTION_FIELD_TYPES: InputComponents[] = [
  InputComponents.TEXT,
  InputComponents.NUMBER,
  InputComponents.SELECT,
  InputComponents.CHECKBOX,
  InputComponents.RADIO,
  InputComponents.DATE,
  InputComponents.SWITCH,
  InputComponents.RESOURCE,
  InputComponents.ESIGN
];

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type NotificationPayload = {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
};

type InstructionFile = {
  question: string;
  description: string;
  file_name: string;
  file_path: string;
  mime_type: string;
  public_url: string;
};


type InstructionsFormProps = {
  selectedNodeData: IINSTRUCTIONS;
  selectedNode: any;
  onSubmit: (data: IINSTRUCTIONS) => void;
  onClose: () => void;
};

const InstructionsForm = memo(({ selectedNodeData, onSubmit, onClose, selectedNode }: InstructionsFormProps) => {
  const [initialValues, setInitialValues] = useState<IINSTRUCTIONS | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  // Link-style media attachments (the 'ATTACHMENT' content mode). Held outside Formik and
  // merged in on submit — same approach the announcement form uses with AttachmentUploader.
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: []
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  const { uploadAsset } = useApi();

  // Memoize the callback to prevent re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
     
    setStatuses(list);
  }, []);

  // Memoize notification change handler to prevent re-renders
  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
     
    setNotificationPayload(payload);
  }, []);

  // Memoize escalation change handler to prevent re-renders
  const handleEscalationChange = useCallback((payload: any) => {
    
    setEscalationPayload(payload);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: any, values: any) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
  
    const uploadedFiles: InstructionFile[] = [];

    for (const file of files) {
     

      if (file.type !== 'application/pdf') {
        alert('Only PDF files are allowed.');
        continue;
      }
      
      setUploadingFile(true);
      try {
        const response = await uploadAsset(file, 'instructions/documents');
        
        
        // Check the response structure from uploadAsset
        const uploadData = response.data || response;
         
        
        if (!uploadData.file_path && !uploadData.public_url && !uploadData.path && !uploadData.url) {
          console.error('❌ Upload failed: Missing file info from server. Response:', uploadData);
         
          continue;
        }
        
        const fileData = {
          question: '',
          description: '',
          file_name: file.name,
          file_path: uploadData.file_path || uploadData.path || '',
          mime_type: file.type,
          public_url: uploadData.public_url || uploadData.url || ''
        };
        
         
        uploadedFiles.push(fileData);
        
      } catch (error) {
        console.error(' File upload failed for', file.name, ':', error);
        alert('File upload failed. Please try again.');
      } finally {
        setUploadingFile(false);
      }
    }

    if (uploadedFiles.length > 0) {
      console.log(' Successfully uploaded', uploadedFiles.length, 'files:', uploadedFiles);
      
      // Merge with existing files - use supportfiles to match backend structure
      const existingFiles = values.settings?.supportfiles || values.settings?.files || [];
      
      
      const updatedFiles = [...existingFiles, ...uploadedFiles];
      
      
      setFieldValue('settings.supportfiles', updatedFiles);
      setFieldValue('settings.files', updatedFiles);
      
      // Also update uploadedDocument with the first file name for display
      if (uploadedFiles.length > 0) {
        setFieldValue('settings.uploadedDocument', uploadedFiles[0].file_name);
      }
      
      
    } else {
      console.log('📁 No files were successfully uploaded');
    }
    
    // Reset the input
    event.target.value = '';
  }, [uploadAsset]);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    console.log('🔄 Step type changing to:', newType);
    const type = newType.toUpperCase();
    setFieldValue('stepType', type);
    setFieldValue('componentType', type);
    setFieldValue('data.title', type);
    setTimeout(() => {
      submitForm();
    });
  }, []);

  useEffect(() => {
    if (!selectedNodeData) return;

    const {
      name = selectedNodeData.component?.name || '',
      title,
      description,
      settings,
      component = {},
      componentType,
      data,
      onComplete,
      fields: savedFields = []
    } = selectedNodeData;

    const actor = toActorUids(selectedNode?.data?.step?.actor);

    // Which content mode to open in. Honour an explicit contentType; otherwise derive it
    // from whatever a legacy step already carries so it reopens showing the same thing:
    // text wins (old display was text-first), then attachment, then PDF, else text.
    const existingAttachments: AnnouncementAttachment[] = Array.isArray(settings?.attachments)
      ? (settings.attachments as AnnouncementAttachment[])
      : [];
    const hasPdf = (settings?.supportfiles?.length || settings?.files?.length) ? true : false;
    const derivedContentType: 'TEXT' | 'PDF' | 'ATTACHMENT' =
      settings?.contentType ||
      (settings?.instruction ? 'TEXT' : existingAttachments.length ? 'ATTACHMENT' : hasPdf ? 'PDF' : 'TEXT');
    setAttachments(existingAttachments);

    // Extract and map notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    const mappedNotification = {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };

    setInitialValues({
      name: name || 'Instructions',
      title: title || 'Instructions',
      description: description || '',
      settings: {
        instruction: settings?.instruction || '',
        uploadedDocument: typeof settings?.uploadedDocument === 'string' ? settings.uploadedDocument : '',
        contentType: derivedContentType,
        requireView: typeof settings?.requireView === 'boolean' ? settings.requireView : true,
        supportfiles: settings?.supportfiles || settings?.files || [],
        files: settings?.supportfiles || settings?.files || []
      },
      component,
      fields: savedFields,
      componentType: componentType || 'INSTRUCTIONS',
      data: {
        title: selectedNode?.data?.label || data?.title || 'Instructions',
        step: { ...(data?.step || {}), actor, componentType: componentType || 'INSTRUCTIONS' }
      },
      actor: actor,
      stepType: selectedNode?.data?.step?.stepType || 'INSTRUCTIONS',
      onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete: onComplete || {}
    });

    // Set notification payload separately to avoid type issues
    setNotificationPayload({
      conditions: [
        ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
        ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
      ],
      users: mappedNotification.notifyUsers || [],
      roles: mappedNotification.notifyRoles || [],
      channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
        ? mappedNotification.notificationChannel.map((channel: string) => 
            channel === 'In-App' ? 'IN-APP' : 
            channel === 'Email' ? 'EMAIL' :
            channel === 'SMS' ? 'SMS' : channel.toUpperCase()
          )
        : []
    });

    // Initialize escalation payload from existing node data
    setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.
  // selectedNodeData.fields included so a flow reload (or a save that comes
  // back with server-assigned field uids) refreshes the list instead of
  // leaving the form on the stale copy it initialised with.
  }, [selectedNodeData.componentType, selectedNodeData.name, selectedNodeData.description, selectedNodeData.settings, selectedNodeData.fields, selectedNode?.data?.label, selectedNode?.data?.step?.actor, selectedNode?.data?.step?.notification, selectedNode?.data?.step?.escalation, selectedNode?.data?.step?.onComplete?.status?.uid]);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  const memoizedFormContent = useMemo(() => {
    if (!initialValues) {
      return (
        <Box sx={{ padding: 2 }}>
          <Typography>Loading Instructions form...</Typography>
        </Box>
      );
    }

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
           
          
          const selectedStatus = statuses.find(s => s.uid === values.onCompleteStatusUid);
          console.log('✅ Selected status:', selectedStatus);

          // Same strip the announcement form applies: `url` is a short-lived preview URL the
          // API rejects, and `media_type` is derived server-side from the mime type — except
          // on a pasted link, which has no mime type and states its own kind.
          const payloadAttachments = attachments.map(({ url, media_type, ...rest }: any) =>
            rest.is_link ? { ...rest, media_type } : rest
          );

          const payload: IINSTRUCTIONS = {
            ...values,
            fields: values.fields || [],
            settings: {
              instruction: values.settings?.instruction || '',
              uploadedDocument: typeof values.settings?.uploadedDocument === 'string' ? values.settings.uploadedDocument : '',
              contentType: values.settings?.contentType || 'TEXT',
              requireView: values.settings?.requireView !== false,
              supportfiles: values.settings?.supportfiles || values.settings?.files || [],
              attachments: payloadAttachments
            },
            component: {
              ...values.component,
              name: values.name,
              componentType: values.componentType || 'INSTRUCTIONS'
            },
            onComplete: selectedStatus ? { status: selectedStatus } : {}
          };

          // Add notification and escalation to the payload (even if not in IINSTRUCTIONS type)
          const finalData = {
            ...payload,
            notification: notificationPayload,
            escalation: escalationPayload
          };

          console.log('📦 Final Instructions Payload Sent:', finalData);
          onSubmit(finalData as IINSTRUCTIONS);
          setSubmitting(false);
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitForm }) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', elevation: 0 }}>
            <Paper elevation={0}  sx={{ p:2, width: '100%', elevation: 0, }}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={3} borderBottom={1} borderColor="grey.400" pb={2}>
                {isEditingTitle ? (
                  <Box display="flex" alignItems="center" sx={{ width: '100%', maxWidth: '600px' }}>
                    <TextField
                      name="data.title"
                      value={values.data?.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                      fullWidth
                      sx={{ mr: 2 ,
                        textTransform: 'uppercase'
                      }}
                    />
                    <Button variant="contained" size="small" onClick={() => setIsEditingTitle(false)}>
                      save
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                      <Typography 
                        variant="h5" 
                        fontWeight={500}
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textTransform: 'uppercase'
                        }}
                        title={values.data?.title || values.stepType}
                      >
                        {values.data?.title || values.stepType || 'Instructions'}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setIsEditingTitle(true)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {selectedNode && (
                      <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                        <StepModify
                          selectedNode={selectedNode}
                          onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
           
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ mt: -1 }}>
                    <FormControl>
                      <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Content Type</FormLabel>
                      <RadioGroup
                        row
                        name="settings.contentType"
                        value={values.settings?.contentType || 'TEXT'}
                        onChange={(e) => setFieldValue('settings.contentType', e.target.value)}
                      >
                        <FormControlLabel value="TEXT" control={<Radio size="small" />} label="Text" />
                        <FormControlLabel value="PDF" control={<Radio size="small" />} label="PDF" />
                        <FormControlLabel value="ATTACHMENT" control={<Radio size="small" />} label="Attachment" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {values.settings?.contentType === 'TEXT' && (
                  <Grid item xs={12} sx={{ mt: -1 }}>
                    <TextField
                      fullWidth
                      label="Instruction"
                      name="settings.instruction"
                      value={values.settings?.instruction || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      minRows={4}
                      placeholder="Enter the instruction text that will be displayed to users..."
                      size="small"
                    />
                  </Grid>
                  )}

                  {values.settings?.contentType === 'ATTACHMENT' && (
                  <Grid item xs={12} sx={{ mt: -1 }}>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                      Attachment
                    </Typography>
                    <AttachmentUploader value={attachments} onChange={setAttachments} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        Require full view before continue
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="settings.requireView"
                            checked={values.settings?.requireView !== false}
                            onChange={(e) => setFieldValue('settings.requireView', e.target.checked)}
                            color="primary"
                            size="medium"
                          />
                        }
                        label=""
                      />
                    </Box>
                  </Grid>
                  )}

                  {values.settings?.contentType === 'PDF' && (
                  <Grid item xs={12} sx={{ mt: -2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography variant="body1" fontWeight="medium">
                          Upload Document
                        </Typography>
                        <Box>
                          <input
                            type="file"
                            id="document-upload"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileSelect(e, setFieldValue, values)}
                            accept=".pdf,image/jpeg,image/png"
                            multiple
                            disabled={uploadingFile}
                          />
                          <label htmlFor="document-upload">
                            <Button
                              component="span"
                              variant="outlined"
                              startIcon={<CloudUpload />}
                              color="primary"
                              disabled={uploadingFile}
                              sx={{ width: 140 }}
                            >
                              {uploadingFile ? 'Uploading...' : 'Upload'}
                            </Button>
                          </label>
                        </Box>
                      </Box>
                      {/* Display uploaded files */}
                      {((values.settings?.supportfiles && values.settings.supportfiles.length > 0) || 
                        (values.settings?.files && values.settings.files.length > 0)) && !uploadingFile && (
                        <Box sx={{ mt: 2, width: '100%' }}>
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium', mb: 1, display: 'block' }}>
                            Uploaded Files ({(values.settings?.supportfiles || values.settings?.files || []).length}):
                          </Typography>
                          {(values.settings?.supportfiles || values.settings?.files || []).map((file: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                mt: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                elevation: 0,
                                backgroundColor: 'grey.50',
                                '&:hover': {
                                  backgroundColor: 'grey.100'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <InsertDriveFile sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={file.file_name || 'Unknown file'}
                                >
                                  {file.file_name || 'Unknown file'}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                onClick={() => {
                                  const currentFiles = values.settings?.supportfiles || values.settings?.files || [];
                                  const updatedFiles = currentFiles.filter((_: any, i: number) => i !== index);
                                  setFieldValue('settings.supportfiles', updatedFiles);
                                  setFieldValue('settings.files', updatedFiles);
                                  // Update uploadedDocument if removing the displayed file
                                  if (values.settings?.uploadedDocument === (file.file_name || file.name)) {
                                    setFieldValue('settings.uploadedDocument', updatedFiles.length > 0 ? updatedFiles[0].file_name : '');
                                  }
                                }}
                                sx={{ 
                                  minWidth: 'auto', 
                                  p: 0.5, 
                                  fontSize: '0.7rem',
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.lighter'
                                  }
                                }}
                                startIcon={<Delete sx={{ fontSize: 14 }} />}
                              >
                                Remove
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {/* Legacy single file display - keep for backward compatibility */}
                      {values.settings?.uploadedDocument && !uploadingFile && 
                       !((values.settings?.supportfiles && values.settings.supportfiles.length > 0) || 
                         (values.settings?.files && values.settings.files.length > 0)) && (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium', mt: 1, textAlign: 'right' }}>
                          {values.settings.uploadedDocument}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  )}
                </Grid>

                {/* Fields the user answers alongside the instruction. Same
                    builder the FORM step uses, so a field behaves identically
                    in both places. */}
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Box borderTop={1} borderColor="grey.300" pt={2}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Fields
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Answers are collected with the acknowledgement and written
                      onto the document. Add an ESIGN field to require a signature.
                    </Typography>
                    <FieldListBuilder
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      selectedNodeData={selectedNodeData}
                      selectedNode={selectedNode}
                      clientUID={selectedNode?.client?.uid || selectedNode?.data?.client?.uid}
                      allowedTypes={INSTRUCTION_FIELD_TYPES}
                      addLabel="Add field"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <CommonNodeSetting
                    selectedNode={selectedNode}
                    onStatusesLoaded={handleStatusesLoaded}
                    onNotificationChange={handleNotificationChange}
                    notificationInitialValues={notificationInitialValues}
                    escalationPayload={escalationPayload}
                    onEscalationChange={handleEscalationChange}
                  />
                </Grid>

                <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                  <Button type="button" color="error" sx={{ py: 1, fontSize: '1rem' }} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary" sx={{ py: 1, fontSize: '1rem' }}>
                   Save & Close
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Formik>
    );
  }, [initialValues, isEditingTitle, onClose, onSubmit, selectedNode, handleStepTypeChange, handleStatusesLoaded, handleNotificationChange, notificationInitialValues, statuses, notificationPayload, escalationPayload, attachments, uploadingFile]);

  return memoizedFormContent;
});


export default InstructionsForm;
